import React, {
  useEffect,
  useCallback,
  useContext,
  useState,
  useRef,
} from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  makeStyles,
  Typography as TypographyMUI,
  Divider as DividerMUI,
} from '@material-ui/core';
import { Button } from '@redshelf/redshelf-ui';

import { SCREEN_SIZE_CATEGORIES, STATUS_CODES } from 'constants';
import { SearchBar, StatusMessage } from 'components';
import {
  BookModifiersContext,
  BookValuesContext,
  BookLocationModifiersContext,
  UserSessionModifiersContext,
  UserSessionValuesContext,
  ViewportValuesContext,
} from 'contexts';
import { SearchResults } from 'types';
import NoContentMessage from '../NoContentMessage';
import { CloudOff } from '@material-ui/icons';
import { useNetworkStatus } from 'hooks';
import SearchResultListItem from './SearchResultListItem';
import SearchResultSingleListItem from './SearchResultSingleListItem';

const useStyles = makeStyles((theme) => {
  return {
    searchContainer: {
      ...theme.typography.body2,
      '& em, & span.found': {
        backgroundColor: theme.palette.greyBlue[100],
        fontStyle: 'normal',
        border: `2px solid ${theme.palette.greyBlue[900]}`,
      },
      overflowY: 'scroll',
    },
    offlineStatusMessage: {
      padding: `${theme.spacing(2)}px 0 ${theme.spacing(3)}px`,
    },
    title: {
      '& .MuiTypography-body1': {
        color: theme.palette.greyBlue[600],
        padding: theme.spacing(2),
        paddingTop: 0,
      },
    },
    unindexedMessage: {
      padding: theme.spacing(1.5),
    },
  };
});

const NoResultsToDisplay = () => {
  return (
    <>
      <DividerMUI />
      <NoContentMessage
        title="No results found"
        content="Check your spelling or try another search."
      />
    </>
  );
};

const SearchResultsSummary = ({ numResults }) => {
  if (!numResults) {
    return <></>;
  }
  return (
    <>
      <TypographyMUI variant="body1">
        Displaying <span className="noTranslate">{numResults}</span>{' '}
        {numResults !== 1 ? 'results' : 'result'}
      </TypographyMUI>
      <DividerMUI />
    </>
  );
};

const SearchResultsContainer = ({
  areAllMarkersMissing,
  isMobile,
  onClose,
  searchResultGroup,
  spotlightSearchResult,
}) => {
  return areAllMarkersMissing ? (
    searchResultGroup.searchResultItems.map((singleSearchResult, index) => {
      return (
        <SearchResultSingleListItem
          key={`${singleSearchResult.key}-${index}`}
          singleSearchResult={singleSearchResult}
          isMobile={isMobile}
          onClose={onClose}
          onSuccessfulNavigateToSearchResult={spotlightSearchResult}
        />
      );
    })
  ) : (
    <SearchResultListItem
      isMobile={isMobile}
      onClose={onClose}
      searchResultGroup={searchResultGroup}
      onSuccessfulNavigateToSearchResult={spotlightSearchResult}
    />
  );
};

const NotProcessedMessage = ({ reloadBook, bookProviderIdle }) => {
  const classes = useStyles();
  return (
    <div className={classes.unindexedMessage}>
      This title is currently being processed for search.
      <br />
      Please try again.
      <br />
      <br />
      <Button
        onClick={reloadBook}
        disabled={!bookProviderIdle}
        variant="primary"
        aria-label="click to reload the eReader and try again to search the book"
      >
        Retry
      </Button>
    </div>
  );
};

const SearchMenu = ({ className, onClose = () => {}, ...props }) => {
  const classes = useStyles();
  const [searchResults, setSearchResults] = useState(null);
  const [numResults, setNumResults] = useState(null);
  const [areAllMarkersMissing, setAreAllMarkersMissing] = useState(null);
  const [string, setString] = useState('');
  const { setRawSpotlightData } = useContext(BookLocationModifiersContext);
  const { book, bookProviderIdle, getDocumentInformation, getLunrSearchIndex } =
    useContext(BookValuesContext);
  const { reloadBook } = useContext(BookModifiersContext);
  const { isOffline } = useNetworkStatus();
  const { useElasticsearchInsteadOfLunr } = useContext(
    UserSessionValuesContext,
  );
  const { setIsSessionExpired } = useContext(UserSessionModifiersContext);
  const { viewport } = useContext(ViewportValuesContext);
  const isMobile = viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.PHONE;
  const inputElementRef = useRef(null);
  const lunrSearchResultsRef = useRef();

  useEffect(() => {
    const handleResize = () => {
      if (
        isMobile &&
        inputElementRef.current &&
        document.activeElement === inputElementRef.current
      ) {
        document.body.scrollTop = 0;
        window.scrollTo(0, 0);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, [isMobile]);

  const handleServerSearchSubmit = useCallback(async () => {
    const response = await fetch(
      `/api/v2/htmlpagetext/?document_id=${book.pk}&search_term=${string}&limit=250`,
    );

    if (response.status === STATUS_CODES.FORBIDDEN) {
      setIsSessionExpired(true);
      return;
    }

    const elasticSearchResults = await response.json();
    const searchResults = new SearchResults({
      book,
      queryString: string,
    }).initializeWithElasticResults(elasticSearchResults);

    setAreAllMarkersMissing(searchResults.hasNoMarkers);
    setNumResults(searchResults.resultsCount);
    setSearchResults(searchResults.resultsAsKeyValueArrayByMarker);
  }, [book, string, setIsSessionExpired]);

  const currentLunrSearchAttemptedString = useRef('');
  const searchResultsPromiseCache = useRef({});

  const handleLunrSearchSubmit = useCallback(async () => {
    // use this so that the reader can read the same search result from the screen,
    // without this, when the results do not change, aria-live will not pick up the changes
    // and make the reader pronounce the text.
    setNumResults(null);

    currentLunrSearchAttemptedString.current = string;

    const lunrSearchIndex = await getLunrSearchIndex();
    lunrSearchResultsRef.current = lunrSearchIndex.search(string.trim());

    if (currentLunrSearchAttemptedString.current !== string) {
      // If the user attempted a second search before the index resolved, we should ignore the first search results.
      return;
    }

    // Cache, or pull from cache, the search results promise for this string.
    if (!searchResultsPromiseCache.current[string]) {
      searchResultsPromiseCache.current[string] = new SearchResults({
        book,
        queryString: string,
      }).initializeWithLunrResults(
        getDocumentInformation,
        lunrSearchResultsRef.current,
      );
    }

    const searchResults = await searchResultsPromiseCache.current[string];

    if (currentLunrSearchAttemptedString.current !== string) {
      // Similar to the return condition above, if the user attempted another search before the results resolved, we should ignore the results.
      return;
    }

    setAreAllMarkersMissing(searchResults.hasNoMarkers);
    setNumResults(searchResults.resultsCount);
    setSearchResults(searchResults.byScore);
  }, [book, getDocumentInformation, getLunrSearchIndex, string]);

  const handleSearchChange = useCallback((event) => {
    setString(event.target.value);
  }, []);

  const useLunrSearch =
    !useElasticsearchInsteadOfLunr && !book.useElasticsearchInsteadOfLunr;

  console.log('searchengine:', useLunrSearch ? 'lunr' : 'elasticsearch'); // Will remove after full Lunr switchover

  const isSearchReady = useLunrSearch
    ? book.hasLunrSearchIndex // check lunr search index availability if the lunr search local setting flag is enabled
    : book.isIndexedForSearch; // otherwise, refer to the elasticsearch index availability

  const handleSearchSubmit = useLunrSearch
    ? handleLunrSearchSubmit // callback to search with lunr
    : handleServerSearchSubmit; // callback to search using the backend

  const spotlightSearchResult = useCallback(
    (searchResult, isFromElasticSearchResult) => {
      setRawSpotlightData({
        lunrSearchResults: lunrSearchResultsRef.current,
        searchResult,
        isFromElasticSearchResult,
      });
    },
    [setRawSpotlightData],
  );

  return (
    <div className={clsx(classes.searchContainer, className)} {...props}>
      {isOffline ? (
        <StatusMessage
          className={classes.offlineStatusMessage}
          icon={<CloudOff />}
          title="You're Offline"
        >
          While offline, you will not be able to search the contents of this
          material.
        </StatusMessage>
      ) : (
        <>
          {isSearchReady ? (
            <>
              <SearchBar
                ref={inputElementRef}
                onChange={handleSearchChange}
                onSubmit={handleSearchSubmit}
                value={string}
              />
            </>
          ) : (
            <NotProcessedMessage
              reloadBook={reloadBook}
              bookProviderIdle={bookProviderIdle}
            />
          )}
          <div className={classes.title} aria-atomic="true" role="alert">
            {numResults === null ? (
              ''
            ) : numResults === 0 ? (
              <NoResultsToDisplay />
            ) : (
              <SearchResultsSummary numResults={numResults} />
            )}
          </div>
          <>
            {searchResults?.length ? (
              <ul>
                {useLunrSearch
                  ? searchResults.map((searchResult) => {
                      return (
                        <SearchResultSingleListItem
                          key={`${searchResult.key}`}
                          singleSearchResult={searchResult}
                          isMobile={isMobile}
                          onClose={onClose}
                          includeLocationInfo={true}
                          onSuccessfulNavigateToSearchResult={
                            spotlightSearchResult
                          }
                        />
                      );
                    })
                  : searchResults.map((searchResultGroup) => {
                      return !searchResultGroup.marker ? null : (
                        <SearchResultsContainer
                          key={`search-results-for-marker-${searchResultGroup.marker.index}-${searchResultGroup.marker.label}`}
                          areAllMarkersMissing={areAllMarkersMissing}
                          isMobile={isMobile}
                          onClose={onClose}
                          searchResultGroup={searchResultGroup}
                          spotlightSearchResult={(searchResult) =>
                            spotlightSearchResult(searchResult, true)
                          }
                        />
                      );
                    })}
              </ul>
            ) : null}
          </>
        </>
      )}
    </div>
  );
};

SearchMenu.propTypes = {
  className: PropTypes.string,
};

export default SearchMenu;
